// Desc: This file contains the filter component for the Asset Map page. It contains the filter fields for the Asset Map page.
import { useState } from "react";
import { Box, Card, CardContent } from "@mui/material";
import { getClassificationsByType } from "../../../utils/getClassificationsByType";
import { getCustomsByType } from "../../../utils/getCustomsByType";
import { isEqual } from "lodash";
import { isValidDateObject } from "../../../globalStore/utils/helpers";
import { searchAssets } from "../../../utils/API/Assets/Assets";
import { useSelector } from "react-redux";
import AutocompleteSelect from "../../../components/Forms/FieldTypes/AutocompleteSelect";
import ClassificationFilters from "../../../components/Selects/ClassificationsSelector/ClassificationFilters";
import CustomFilters from "../../../components/Selects/CustomsSelector/CustomFilters";
import Grid from "@mui/material/Grid2";
import Loading from "../../../components/Loading/Loading";
import LXDateRangePicker from "../../../components/Forms/FieldTypes/DateRangePicker";
import MaterialUiButton from "../../../components/Buttons/MaterialUiButton/MaterialUiButton";
import SimpleTextField from "../../../components/Forms/FieldTypes/TextField";

export default function AssetMapFilter(props) {
  const { apiUrl, organizationId, setState, state, token } = props;
  const { filters = {} } = state;
  const { endDate, startDate, tag, facility = { name: "" } } = filters;

  const { facilities } = useSelector(
    (state) => state.organization,
    isEqual
  );

  const facilityArray = facilities
    ? Object.values(facilities).sort((a, b) => {
      return (a.name || "").localeCompare(b.name || "");
    })
    : [];

  const classificationsList = getClassificationsByType({
    assetMode: "Asset",
    isObject: false
  });

  const customsList = getCustomsByType({
    assetMode: "Asset",
  });

  const [canSubmit, setCanSubmit] = useState(true);
  //filterLoading variable to show loading spinner when filters are being applied
  const [filterLoading, setFilterLoading] = useState(false);

  function handleFilterApply() {
    setFilterLoading(true);
    //use searchAssets to filter the data in the map
    searchAssets(
      {
        apiUrl,
        token,
        organizationId,
        assetMode: "Asset",
        facilityId: state.filters.facility?.facilityId,
      },
      { filters: state.filters }
    ).then((results) => {
      setFilterLoading(false);
      setState((prevState) => {
        return {
          ...prevState,
          assets: results.assets,
          count: results.count,
          page: Math.floor(state.filters.start / state.filters.limit),
          mapTableData: [],
        };
      });
    });
  }

  const fieldStyle = { margin: "1rem 0" };

  return (
    (<Card>
      <CardContent>
        <Grid container>
          {/* //spinner to show loading when filters are being applied */}
          {filterLoading && (
            <Loading
              styles={{
                position: "absolute",
                top: "40%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            />
          )}

          {/* Date range (From – To) / Display Button */}
          <Grid sx={{ padding: "0 1rem" }} size={3}>
            <Box sx={fieldStyle}>
              <LXDateRangePicker
                format="MM/dd/yyyy"
                label="From – To Date (required)"
                onChange={(start, end) => {
                  const startValid = isValidDateObject(start);
                  const endValid = isValidDateObject(end);
                  const rangeValid =
                    !start ||
                    !end ||
                    start <= end ||
                    start?.toDateString?.() === end?.toDateString?.();
                  setCanSubmit(startValid && endValid && rangeValid);
                  setState((prevState) => ({
                    ...prevState,
                    filters: {
                      ...prevState.filters,
                      startDate: start ?? prevState.filters.startDate,
                      endDate: end ?? prevState.filters.endDate,
                    },
                  }));
                }}
                slotProps={{
                  textField: {
                    helperText: !canSubmit && "Invalid date range",
                    size: "large",
                  },
                }}
                value={[startDate, endDate]}
              />
            </Box>

            {/* Display Button */}
            <Box sx={{ marginTop: "1rem" }}>
              <MaterialUiButton
                label="Apply Filters"
                data-testid="Display Button"
                onClick={() => handleFilterApply()}
              />
            </Box>
          </Grid>

          {/* Row/Bin / Event */}
          <Grid sx={{ padding: "0 1rem" }} size={3}>
            {/* Tag ID */}
            <Box sx={fieldStyle}>
              <SimpleTextField
                onChange={(event) =>
                  setState((prevState) => ({
                    ...prevState,
                    filters: {
                      ...prevState.filters,
                      tag: event.target.value,
                    },
                  }))
                }
                fullWidth
                size="large"
                value={tag}
                label="Tag Id"
              />
            </Box>

            {/* Facility */}
            <Box sx={fieldStyle}>
              <AutocompleteSelect
                id="facilityName"
                getOptionLabel={(option) => option.name}
                label="Facility Name"
                onChange={(event, newValue) => {
                  setState((prevState) => ({
                    ...prevState,
                    filters: {
                      ...prevState.filters,
                      facility: newValue,
                    },
                  }));
                }}
                options={facilityArray}
                size="large"
                value={facility}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Classification Filters */}
          <ClassificationFilters
            classifications={classificationsList}
            state={state}
            setState={setState}
          />

          {/* Custom Filters */}
          <CustomFilters
            customs={customsList}
            state={state}
            setState={setState}
          />
        </Grid>
      </CardContent>
    </Card>)
  );
}
